import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const Hero = () => {
  const navigate = useNavigate();
  return (
    <section
      id="hero"
      data-testid="landing-hero"
      className="relative w-full overflow-hidden bg-[#04302E] text-white"
      style={{ minHeight: "calc(100vh - 72px)" }}
    >
      {/* Background video (with image poster fallback) */}
      <video
        data-testid="hero-bg-video"
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster="https://images.pexels.com/videos/8685430/pexels-photo-8685430.jpeg?auto=compress&cs=tinysrgb&w=2400"
      >
        <source
          src="https://videos.pexels.com/video-files/8685430/8685430-hd_1920_1080_25fps.mp4"
          type="video/mp4"
        />
        <source
          src="https://videos.pexels.com/video-files/8716788/8716788-uhd_2560_1440_25fps.mp4"
          type="video/mp4"
        />
        <source
          src="https://videos.pexels.com/video-files/8631869/8631869-uhd_2560_1440_25fps.mp4"
          type="video/mp4"
        />
        <source
          src="https://videos.pexels.com/video-files/7490403/7490403-hd_1920_1080_30fps.mp4"
          type="video/mp4"
        />
      </video>

      {/* Dark scrim for legibility — heavier on the left where text sits */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50"
      />

      {/* Content */}
      <div className="relative mx-auto flex max-w-7xl items-center px-6 py-24 lg:py-32" style={{ minHeight: "calc(100vh - 72px)" }}>
        <div className="max-w-3xl">
          <p data-testid="hero-tagline" className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-300/90 drop-shadow">
            Unlock Your Capital Potential
          </p>

          <h1
            data-testid="hero-heading"
            className="mt-5 font-extrabold leading-[1.05] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)] break-words"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            <span
              className="block"
              style={{ fontSize: "clamp(1.875rem, 6.2vw, 4.25rem)" }}
            >
              Is your business well-positioned to{" "}
              <span className="text-emerald-400">secure capital</span>?
            </span>
            <span
              className="mt-4 block text-white/85 font-bold"
              style={{ fontSize: "clamp(1.125rem, 3vw, 2rem)" }}
            >
              Get your <span className="text-emerald-300">capital readiness score</span> in 5 minutes — and discover which funder is most likely to back your business.
            </span>
          </h1>

          <p
            data-testid="hero-subtext"
            className="mt-6 max-w-xl text-base text-white/85 sm:text-lg drop-shadow"
          >
            Clinq scores African businesses for capital readiness and matches them with the
            right funders — Angel Investors, VCs, Private Equity, Banks, and DFIs. Find out
            where you stand today.
          </p>

          <div className="mt-10 flex w-full max-w-md flex-col items-stretch gap-4 sm:w-auto sm:max-w-none sm:inline-flex">
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                data-testid="hero-get-funded-btn"
                onClick={() => navigate("/get-started")}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 transition-all hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20 sm:w-auto sm:justify-start"
              >
                Start free — get my business score
                <ArrowRight className="h-4 w-4 text-emerald-300 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                data-testid="hero-fund-project-btn"
                onClick={() => navigate("/get-started-project")}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-white px-6 py-3 text-sm font-semibold text-[#004B62] shadow-lg shadow-black/30 transition-all hover:bg-slate-100 sm:w-auto sm:justify-start"
              >
                Start free — score my project
                <ArrowRight className="h-4 w-4 text-[#004B62] transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
            <button
              data-testid="hero-start-investing-btn"
              onClick={() => navigate("/get-started-funder")}
              className="w-full rounded-md border border-white/30 bg-white/10 px-6 py-3 text-center text-sm font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/20"
            >
              I want to explore/invest in Funder-Ready Opportunities &rarr;
            </button>
          </div>

          {/* Trust strip */}
          <div
            data-testid="hero-trust-strip"
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium uppercase tracking-widest text-white/60"
          >
            <span>Angels · Local · VC · PE · Banks · DFIs</span>
            <span className="h-1 w-1 rounded-full bg-white/30"/>
            <span>25 African markets</span>
            <span className="h-1 w-1 rounded-full bg-white/30"/>
            <span>Equity · Debt · Mezzanine · Grants</span>
          </div>
        </div>
      </div>
    </section>
  );
};
